import { z } from 'zod';
/**
 * Itinerary input validation, the D1 lifecycle invariant, and the slug
 * generator — the single source of truth consumed by U4 (router) and U7 (clone).
 *
 * Scope (plan SG-03 / Key Technical Decision D1): Phase 1+2 ships ONLY
 * `ownerType='user'` content. The cross-field invariant branches for
 * `wineryxplorer`/`venue` ownership are intentionally NOT implemented here —
 * they land in Phase 4/5 alongside their routers.
 */
// ---------------------------------------------------------------------------
// Enum mirrors (kept in lock-step with packages/database Itinerary.ts; shared
// has no runtime dependency on @wineryxplorer/database so the union is restated
// here rather than imported).
// ---------------------------------------------------------------------------
export const ITINERARY_OWNER_TYPES = ['user', 'wineryxplorer', 'venue'];
export const ITINERARY_AUDIENCES = ['private', 'unlisted', 'public'];
export const ITINERARY_EDITORIAL_STATUSES = ['draft', 'published', 'archived'];
export const ITINERARY_STOP_REF_TYPES = ['venue', 'place'];
/**
 * A stop's `refType` and its payload agree: a venue stop carries a `venueId`
 * (and no place), a place stop carries a `place` (and no venueId). Returns
 * boolean (pair the thin assert with it where a throw is preferred).
 */
export const isValidStopRef = (stop) => {
    if (stop.refType === 'venue') {
        return Boolean(stop.venueId) && !stop.place;
    }
    // refType === 'place'
    return Boolean(stop.place?.placeId) && !stop.venueId;
};
export const assertValidStopRef = (stop) => {
    if (!isValidStopRef(stop)) {
        throw new Error(stop.refType === 'place'
            ? "Invariant violation: refType='place' requires a place payload"
            : "Invariant violation: refType='venue' requires a venueId");
    }
};
// ---------------------------------------------------------------------------
// Reusable field schemas (house conventions: empty-string optional → undefined;
// never z.date(); array of ids filtered before any ObjectId use).
// ---------------------------------------------------------------------------
const objectIdSchema = z.string().min(1, 'id is required');
/** Optional free-text that normalizes '' → undefined. */
const optionalText = z
    .string()
    .optional()
    .transform((v) => v || undefined);
/** Optional id array with empty strings filtered out (pre-ObjectId guard). */
const idArraySchema = z
    .array(z.string())
    .default([])
    .transform((arr) => arr.filter(Boolean));
/**
 * Snapshot of a Google Place picked from search — the payload for a
 * `refType='place'` stop. Coordinates are stored so travel legs and the
 * offline mobile viewer never need a resolution round-trip.
 */
export const itineraryPlacePayloadSchema = z.object({
    placeId: z.string().min(1).max(300),
    name: z.string().min(1).max(300),
    address: z
        .string()
        .max(500)
        .optional()
        .transform((v) => v || undefined),
    lat: z.number().min(-90).max(90),
    lng: z.number().min(-180).max(180),
});
/** Reusable cross-field check: refType agrees with its venue/place payload. */
const refineStopRef = (s, ctx) => {
    if (isValidStopRef(s))
        return;
    ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: s.refType === 'place'
            ? "refType='place' requires a place"
            : "refType='venue' requires a venueId",
        path: [s.refType === 'place' ? 'place' : 'venueId'],
    });
};
/** Stop reference: refType agrees with its venue/place payload. */
const stopRefSchema = z
    .object({
    refType: z.enum(ITINERARY_STOP_REF_TYPES).default('venue'),
    venueId: z
        .string()
        .optional()
        .transform((v) => v || undefined),
    place: itineraryPlacePayloadSchema.optional(),
    notes: optionalText,
    arrivalTime: optionalText,
})
    .superRefine(refineStopRef);
// ---------------------------------------------------------------------------
// Mutation input schemas — one per Phase 1+2 itinerary mutation. (U4/U7 consume
// these directly as tRPC `.input()`.)
// ---------------------------------------------------------------------------
export const itineraryCreateSchema = z.object({
    title: z.string().min(1, 'Title is required').max(200),
    description: z
        .string()
        .max(2000, 'Description must be 2000 characters or fewer')
        .optional()
        .transform((v) => (v && v.length > 0 ? v : undefined)),
    regionRefs: idArraySchema,
});
/**
 * Metadata update — title, description, regionRefs. Days/stops/audience are
 * handled by their own dedicated mutations (D4 leg-recompute lives on the
 * stop/day path; D1 audience flip lives on `setAudience`). All fields are
 * optional — omit to leave unchanged. Title cannot be cleared (required at
 * the model layer); description CAN be cleared by passing an empty string.
 */
export const itineraryUpdateMetadataSchema = z.object({
    itineraryId: objectIdSchema,
    title: z.string().min(1).max(200).optional(),
    // Distinguish "not provided" (undefined) from "explicit clear" (empty string).
    // Empty string is preserved so the API can `$set` it to '' and unset.
    description: z.string().max(2000).optional(),
    regionRefs: idArraySchema.optional(),
});
export const itineraryAddStopSchema = z
    .object({
    itineraryId: objectIdSchema,
    dayId: objectIdSchema,
    /** Insert position; omitted → append. */
    ordinal: z.number().int().min(0).optional(),
    refType: z.enum(ITINERARY_STOP_REF_TYPES).default('venue'),
    venueId: z
        .string()
        .optional()
        .transform((v) => v || undefined),
    /** Set for `refType='place'`; omit for venue stops. */
    place: itineraryPlacePayloadSchema.optional(),
    notes: optionalText,
    arrivalTime: optionalText,
    // Planned visit length in minutes. Capped at 24h. Optional; omit to leave unset.
    durationMinutes: z
        .number()
        .int()
        .min(0)
        .max(24 * 60)
        .optional(),
})
    .superRefine(refineStopRef);
export const itineraryRemoveStopSchema = z.object({
    itineraryId: objectIdSchema,
    dayId: objectIdSchema,
    stopId: objectIdSchema,
});
export const itineraryReorderStopSchema = z.object({
    itineraryId: objectIdSchema,
    stopId: objectIdSchema,
    /** Destination day (may differ from source day for a cross-day move). */
    toDayId: objectIdSchema,
    toOrdinal: z.number().int().min(0),
});
export const itineraryUpdateStopSchema = z.object({
    itineraryId: objectIdSchema,
    dayId: objectIdSchema,
    stopId: objectIdSchema,
    venueId: z
        .string()
        .optional()
        .transform((v) => v || undefined),
    notes: optionalText,
    arrivalTime: optionalText,
    // Same shape as on add. `null` is intentionally accepted so callers can
    // explicitly clear the field (Mongoose treats null on $set as removing the
    // value at the document level), while `undefined` means "leave unchanged."
    durationMinutes: z
        .number()
        .int()
        .min(0)
        .max(24 * 60)
        .nullable()
        .optional(),
});
export const itineraryAddDaySchema = z.object({
    itineraryId: objectIdSchema,
    ordinal: z.number().int().min(0).optional(),
    title: optionalText,
});
export const itineraryRemoveDaySchema = z.object({
    itineraryId: objectIdSchema,
    dayId: objectIdSchema,
});
export const itineraryReorderDaySchema = z.object({
    itineraryId: objectIdSchema,
    dayId: objectIdSchema,
    toOrdinal: z.number().int().min(0),
});
export const itineraryUpdateDaySchema = z.object({
    itineraryId: objectIdSchema,
    dayId: objectIdSchema,
    title: optionalText,
    // ISO YYYY-MM-DD. `null` is an explicit clear (date removed from the day);
    // `undefined` means "leave unchanged."
    date: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'date must be YYYY-MM-DD')
        .nullable()
        .optional(),
});
export const itinerarySetAudienceSchema = z.object({
    itineraryId: objectIdSchema,
    // D1: 'publishing' for a USER is an audience flip, gated by canEditItinerary
    // (NOT canPublishItinerary). editorialStatus is never changed here.
    audience: z.enum(ITINERARY_AUDIENCES),
});
export const itineraryArchiveSchema = z.object({
    itineraryId: objectIdSchema,
});
export const itineraryUnarchiveSchema = z.object({
    itineraryId: objectIdSchema,
});
export const itineraryDeleteOwnSchema = z.object({
    itineraryId: objectIdSchema,
});
export const itineraryCloneSchema = z.object({
    // D5: shareable surfaces route by crypto-random publicSlug, never _id.
    sourceSlug: z.string().min(1, 'sourceSlug is required'),
});
// Re-exported so callers can validate stop ref payloads in isolation.
export { stopRefSchema as itineraryStopRefSchema };
// ---------------------------------------------------------------------------
// Lifecycle / ordinal invariants (the convenience layer — D1's REAL enforcement
// is the U1 Mongoose pre('save')/pre(/^findOneAndUpdate/) model guard).
// ---------------------------------------------------------------------------
/**
 * True when `nums` is a 0-indexed, gap-free, duplicate-free run: i.e. the
 * multiset equals exactly {0, 1, ..., n-1}. Empty array is contiguous.
 *
 * @example isContiguousOrdinals([0, 1, 2]) // true
 * @example isContiguousOrdinals([0, 2])    // false (gap)
 * @example isContiguousOrdinals([0, 0])    // false (duplicate)
 */
export const isContiguousOrdinals = (nums) => {
    if (nums.length === 0)
        return true;
    const seen = new Set();
    for (const n of nums) {
        if (!Number.isInteger(n) || n < 0 || n >= nums.length || seen.has(n)) {
            return false;
        }
        seen.add(n);
    }
    return seen.size === nums.length;
};
/**
 * Thin throwing assert over {@link isContiguousOrdinals}. Use server-side after
 * an ordinal rewrite to fail loudly on a contiguity bug.
 */
export const assertContiguousOrdinals = (nums, context = 'ordinals') => {
    if (!isContiguousOrdinals(nums)) {
        throw new Error(`Invariant violation: ${context} must be contiguous 0-indexed (no gaps, no dups); got [${nums.join(', ')}]`);
    }
};
/**
 * D1 convenience assert: a USER-owned itinerary may never be
 * `editorialStatus='draft'`. Mirrors the U1 model write-path guard; the model
 * hook is the actual invariant, this is the early/cheap boundary check.
 *
 * Scoped to `ownerType='user'` for Phase 1+2 (SG-03): non-user ownerTypes are
 * intentionally unconstrained here and validated in Phase 4/5.
 */
export const assertUserLifecycleInvariant = (lifecycle) => {
    if (lifecycle.ownerType === 'user' && lifecycle.editorialStatus === 'draft') {
        throw new Error("Invariant violation: ownerType='user' cannot have editorialStatus='draft' (D1)");
    }
};
// ---------------------------------------------------------------------------
// publicSlug generator (D5) — crypto-random, URL-safe, ~22 chars.
// ---------------------------------------------------------------------------
/**
 * Generate a collision-resistant, URL-safe public slug (~22 chars, 128 bits of
 * entropy). 16 random bytes → base64url, padding stripped. Charset is exactly
 * `[A-Za-z0-9_-]`. Uses the Web Crypto global (`crypto.getRandomValues`),
 * available in Node 18+ and browsers, so this stays bundleable for the web app
 * and keeps the shared package free of runtime dependencies.
 */
export const generateItinerarySlug = () => {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    let binary = '';
    for (const byte of bytes) {
        binary += String.fromCharCode(byte);
    }
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};
